import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiMenu, FiX, FiChevronDown } = FiIcons;

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // desktop dropdown key
  const [mobileOpenMenu, setMobileOpenMenu] = useState(null); // mobile accordion key
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const tools = [
    { name: "IRA 45V Credit Estimator", href: "/interactive-tools/45v-credit-estimator" },
    { name: "Gas Turbine Model Comparison", href: "/interactive-tools/turbine-comparison" },
    { name: "H₂ Fuel Switching Breakeven", href: "/interactive-tools/h2-breakeven" },
    { name: "Data Center Power Sizer", href: "/interactive-tools/data-center-power-sizer" },
    { name: "Carbon Price Modeler", href: "/interactive-tools/carbon-price-impact-modeler" },
    { name: "H2 Blend Optimizer", href: "/interactive-tools/hydrogen-blend-dispatch-optimizer" },
    { name: "Efficiency Map", href: "/interactive-tools/gas-turbine-efficiency-map" },
    { name: "TIC ROI Calculator", href: "/interactive-tools/turbine-inlet-cooling-roi-calculator" },
    { name: "H₂ Material & Lifetime Predictor", href: "/interactive-tools/h2-material-predictor" },
    { name: "Hydrogen Purity vs. NOx Plotter", href: "/interactive-tools/h2-purity-nox-plotter" },
    { name: "Turbine Reliability Benchmarker", href: "/interactive-tools/turbine-reliability-benchmarker" },
    { name: "Diluent Trade-off Calculator", href: "/interactive-tools/diluent-calculator" },
    { name: "Hydrogen Blend Calculator", href: "/interactive-tools/hydrogen-blend-calculator" },
    { name: "Telemetry Simulator", href: "/interactive-tools/telemetry-simulator" },
    { name: "SCADA Dashboard Demo", href: "/interactive-tools/scada-dashboard" },
    { name: "LCOE & Retrofit TCO", href: "/interactive-tools/lcoe-retrofit" },
    { name: "Emissions Estimator", href: "/interactive-tools/emissions-estimator" },
    { name: "Combustion Explorer", href: "/interactive-tools/combustion-explorer" },
    { name: "Grid Peaker Scheduler", href: "/interactive-tools/peaker-scheduler" },
    { name: "H₂-Ready Plant Checklist", href: "/interactive-tools/h2-ready-checklist" },
    { name: "Data Center Reliability Scenarios", href: "/interactive-tools/datacenter-scenarios" },
    { name: "Alarm Rule Sandbox", href: "/interactive-tools/alarm-rules" },
    { name: "Anomaly Detection Playground", href: "/interactive-tools/anomaly-playground" },
    { name: "Tag Mapper", href: "/interactive-tools/tag-mapper" },
  ];

  const navItems = [
    { key: "home", name: "Home", href: "/", isHash: false },
    { key: "tech", name: "Technology", href: "#features", isHash: true },
    { key: "blog", name: "Blog", href: "/blog", isHash: false },
    { key: "about", name: "About", href: "/about", isHash: false },
    {
      key: "tools",
      name: "Interactive Tools",
      href: "/interactive-tools", // hub page
      isHash: false,
      menu: tools,                // signals dropdown/accordion
    },
    { key: "contact", name: "Contact", href: "/contact", isHash: false },
  ];

// replace your go() and isActive() with these

const go = (href, isHash) => {
  if (isHash) {
    const hash = href.startsWith('#') ? href : `#${href}`;
    if (location.pathname !== "/") {
      // navigate to home WITH hash (HashRouter will render /#/... and preserve hash)
      navigate(`/${hash}`);
    } else {
      // already on home → smooth scroll and update hash
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        // keep the URL in sync without pushing history entries
        if (history.replaceState) history.replaceState(null, "", hash);
      }
    }
  } else {
    navigate(href);
  }
  setIsMobileOpen(false);
  setOpenMenu(null);
};

const isActive = (item) => {
  if (item.isHash) {
    // consider a hash item "active" when on home, regardless of the current hash
    return location.pathname === "/";
  }
  return location.pathname === item.href;
};

  const textColor = isScrolled ? "text-secondary-700" : "text-white";

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed z-50 w-full transition-all duration-300 ${
        isScrolled ? "bg-white/90 backdrop-blur shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            className="flex items-center gap-2 focus:outline-none"
            onClick={() => navigate("/")}
          >
            <img src="/favicon.png" alt="GreenGasTurbines logo" className="h-8 w-8" />
            <span className={`text-xl font-bold ${isScrolled ? "text-secondary-900" : "text-white"}`}>
              GreenGasTurbines
            </span>
          </motion.button>

          {/* Desktop Nav */}
          <nav className="relative hidden items-center gap-8 md:flex">
            {navItems.map((item) => {
              const hasMenu = Array.isArray(item.menu);
              if (!hasMenu) {
                return (
                  <button
                    key={item.key}
                    onClick={() => go(item.href, item.isHash)}
                    className={`text-sm font-medium transition-colors hover:text-primary-500 ${textColor} ${
                      isActive(item) ? "text-primary-500" : ""
                    }`}
                  >
                    {item.name}
                  </button>
                );
              }

              // Dropdown trigger
              return (
                <div
                  key={item.key}
                  className="relative"
                  onMouseEnter={() => setOpenMenu(item.key)}
                  onMouseLeave={() => setOpenMenu(null)}
                >
                  <button
                    onClick={() => go(item.href, item.isHash)} // click label → hub
                    className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary-500 ${textColor} ${
                      isActive(item) ? "text-primary-500" : ""
                    }`}
                  >
                    {item.name}
                    <FiChevronDown className="h-4 w-4" />
                  </button>

                  {/* Dropdown */}
                  <AnimatePresence>
                    {openMenu === item.key && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-1/2 mt-2 w-[520px] -translate-x-1/2 rounded-2xl border bg-white p-3 shadow-xl"
                      >
                        <div className="grid grid-cols-2 gap-2">
                          {item.menu.slice(0, 8).map((m) => (
                            <button
                              key={m.href}
                              onClick={() => go(m.href, false)}
                              className="rounded-lg p-3 text-left hover:bg-primary-50"
                            >
                              <div className="text-sm font-medium text-secondary-900">{m.name}</div>
                              <div className="mt-0.5 text-xs text-secondary-600">{m.summary ?? ""}</div>
                            </button>
                          ))}
                        </div>
                        <div className="mt-2 border-t pt-2 text-right">
                          <button
                            onClick={() => go(item.href, false)}
                            className="text-sm font-medium text-primary-600 hover:underline"
                          >
                            View all tools →
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </nav>

          {/* CTA */}
          <motion.button
            onClick={() => go("#quote", true)}
            className="hidden rounded-lg bg-primary-600 px-6 py-2 font-medium text-white hover:bg-primary-700 md:block"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Quote
          </motion.button>

          {/* Mobile toggle */}
          <button 
            className="md:hidden" 
            onClick={() => setIsMobileOpen((v) => !v)}
            aria-label={isMobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileOpen}
          >
            <SafeIcon
              icon={isMobileOpen ? FiX : FiMenu}
              className={`h-6 w-6 ${isScrolled ? "text-secondary-900" : "text-white"}`}
            />
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isMobileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="md:hidden rounded-lg bg-white py-2 shadow-lg"
            >
              {navItems.map((item) => {
                const hasMenu = Array.isArray(item.menu);
                if (!hasMenu) {
                  return (
                    <button
                      key={item.key}
                      onClick={() => go(item.href, item.isHash)}
                      className={`block w-full px-4 py-2 text-left text-secondary-700 hover:bg-primary-50 hover:text-primary-600 ${
                        isActive(item) ? "bg-primary-50 text-primary-600" : ""
                      }`}
                    >
                      {item.name}
                    </button>
                  );
                }

                // Accordion section for Interactive Tools
                const open = mobileOpenMenu === item.key;
                return (
                  <div key={item.key} className="px-2">
                    <button
                      onClick={() => setMobileOpenMenu(open ? null : item.key)}
                      className="flex w-full items-center justify-between rounded-md px-2 py-2 text-left font-medium text-secondary-700 hover:bg-primary-50 hover:text-primary-600"
                    >
                      <span onClick={(e) => { e.stopPropagation(); go(item.href, false); }}>
                        {item.name}
                      </span>
                      <FiChevronDown className={`h-4 w-4 transition ${open ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          {item.menu.map((m) => (
                            <button
                              key={m.href}
                              onClick={() => go(m.href, false)}
                              className="block w-full rounded-md px-4 py-2 text-left text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-600"
                            >
                              {m.name}
                            </button>
                          ))}
                          <div className="px-4 pb-2 pt-1">
                            <button
                              onClick={() => go(item.href, false)}
                              className="text-sm font-medium text-primary-600 hover:underline"
                            >
                              View all tools →
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}

              <div className="px-4 pb-3 pt-1">
                <button
                  onClick={() => go("#quote", true)}
                  className="w-full rounded-lg bg-primary-600 px-4 py-2 font-medium text-white hover:bg-primary-700"
                >
                  Get Quote
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
